import React, { useEffect } from 'react'
import Head from 'next/head'
import { useCookies } from 'react-cookie'

import { PublicUser } from 'services/apiTypes/user.types'

import { setAffiliateDeckLoaded } from 'services/caching.service'

import PageWrapper from 'components/misc/PageWrapper'
import CardBanner from 'components/misc/CardBanner'
import InlineAd from 'components/advertisments/InlineAd'
import DeckLink, { gridClass } from 'components/misc/DeckLink'
import UserInfoAndBioCard from 'components/userPage/UserInfoAndBioCard'
import Header from 'components/userPage/Header'

import styles from './aggregatedUsersLandingPage.module.scss'

export type Props = {
  users: PublicUser[]
  banner: string
  title: string
  description: string
  avatar: string
  adLabel: string
}

const AggregatedUsersLandingPage = ({ users, banner, title, description, avatar, adLabel }: Props) => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  useEffect(() => {
    const referrerEnum: string | null = users.reduce((acc: string | null, u) => acc || u.profile.referrerEnum, null)

    if (referrerEnum && !userId) setAffiliateDeckLoaded(referrerEnum)
  }, [])

  return (
    <PageWrapper title={title} description={description}>
      <Head>
        <meta property="og:type" content="website" />
        <meta property="og:image" content={avatar} />
        <meta property="og:image:width" content="100" />
        <meta property="og:image:height" content="100" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:label1" content="Decks" />
        <meta name="twitter:data1" content={`${users.reduce((acc, u) => acc + u.deckCount, 0)}`} />
        <meta name="twitter:label2" content="Comments" />
        <meta name="twitter:data2" content={`${users.reduce((acc, u) => acc + u.commentCount, 0)}`} />
      </Head>

      <CardBanner src={banner} allowOverflow height="250px" />

      <div className={styles.container}>
        {users.map((user, index) => (
          <React.Fragment key={index}>
            <UserInfoAndBioCard user={user} className={styles.containWidth} />

            <div className={styles.containWidth}>
              <Header
                main="Recent Decks"
                secondary="View all decks"
                href={`/search/decks/?ownerUsername=${user.username}`}
              />
              <div className={gridClass}>
                {user.decks.map((deck, index) => (
                  <DeckLink key={index} deck={deck} />
                ))}
              </div>

              {!user.decks.length && <div className={styles.noDecks}>{user.username} has no public decks</div>}
            </div>
          </React.Fragment>
        ))}
      </div>
    </PageWrapper>
  )
}

export default AggregatedUsersLandingPage
